import { useLazyQuery } from '@apollo/client';

import Pagination, { defaultPagination } from '../../../../models/Pagination';
import MasterEvent, { MasterEventRef } from '../../models/MasterEvent';
import { GET_MASTER_EVENTS } from './query';

export interface FindMasterEventsInput {
  countries?: string[];
  endDate?: Date;
  isArchived?: boolean;
  isOngoing?: boolean;
  isPast?: boolean;
  isUpcoming?: boolean;
  limit: number;
  offset: number;
  searchText?: string;
  sortBy: string;
  sortDirection: string;
  startDate?: Date;
  typeIn?: string[];
  typeNotIn?: string[];
}

interface QueryData {
  result: {
    data: MasterEventRef[];
    pagination: Pagination;
  };
}

interface QueryVars {
  input: FindMasterEventsInput;
}

export interface PaginatedMasterEvents {
  masterEvents: MasterEvent[];
  pagination: Pagination;
}

function getMasterEvents(data: QueryData | undefined): MasterEvent[] {
  return data?.result.data.map((d) => d.value) ?? [];
}

function getPagination(data: QueryData | undefined): Pagination {
  return data?.result.pagination ?? defaultPagination;
}

/**
 * Fetch paginated list of master events
 */
function useFetchMasterEvents() {
  const [execute, { loading, error, data }] = useLazyQuery<QueryData, QueryVars>(
    GET_MASTER_EVENTS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    },
  );

  const fetchMasterEvents = async ({
    sortDirection,
    ...params
  }: FindMasterEventsInput): Promise<PaginatedMasterEvents> => {
    const variables: QueryVars = {
      input: {
        ...params,
        sortDirection: sortDirection.toUpperCase(),
      },
    };

    const response = await execute({
      variables,
    });

    return {
      masterEvents: getMasterEvents(response.data),
      pagination: getPagination(response.data),
    };
  };

  return {
    fetchMasterEvents,
    loading,
    error,
    masterEvents: getMasterEvents(data),
    pagination: getPagination(data),
  };
}

export default useFetchMasterEvents;
